import type { ListBuilderConfiguration } from '../types';

/**
 * Shared execution status value mapping for automation reports
 */
const EXECUTION_STATUS_VALUE_MAPPING = [
	{
		value: 'active',
		label: 'Active',
		color: { bg: '#dbeafe', text: '#1e40af' },
	},
	{
		value: 'completed',
		label: 'Completed',
		color: { bg: '#dcfce7', text: '#166534' },
	},
	{
		value: 'failed',
		label: 'Failed',
		color: { bg: '#fee2e2', text: '#991b1b' },
	},
	{
		value: 'paused',
		label: 'Paused',
		color: { bg: '#fef3c7', text: '#92400e' },
	},
];

/**
 * Automation Reports Trigger Embedded list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const AUTOMATION_REPORTS_TRIGGER_EMBEDDED_CONFIG: ListBuilderConfiguration =
	{
		lists: [
			{
				id: 'default-automation-reports-trigger-embedded',
				cacheTTL: 600000,
				name: 'Trigger Executions',
				type: 'default',
				active: true,
				columns: [
					{
						id: 'execution-id',
						headers: [{ id: 'execution-id-header', text: 'Execution' }],
						fields: [
							{
								id: 'id-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'id',
										objectTable: 'automation_execution',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: { type: 'muted' },
								},
								displayStyle: 'text',
								prefix: '#',
								link: {
									type: 'object',
									enabled: true,
								},
							},
						],
						sortable: true,
						sortFields: [
							{
								objectTable: '_automation_execution',
								fieldName: 'id',
								direction: 'DESC',
							},
						],
						width: 100,
					},
					{
						id: 'flow-name',
						headers: [{ id: 'flow-name-header', text: 'Flow' }],
						fields: [
							{
								id: 'flow-name-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'version.flow.name',
										objectTable: 'automation_execution',
									},
								},
								styleConfig: {
									size: 'md',
									colorRule: { type: 'default' },
								},
								displayStyle: 'text',
								link: {
									type: 'custom',
									enabled: true,
									urlTemplate:
										'/automation/flows/{version.flow.id}/version/{version.id}',
								},
							},
							{
								id: 'line-break-1',
								dataConfig: {
									type: 'custom_text',
									customText: '',
								},
								displayStyle: 'line_break',
							},
							{
								id: 'version-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'version.name',
										objectTable: 'automation_execution',
									},
								},
								styleConfig: {
									size: 'xs',
									colorRule: { type: 'muted' },
								},
								displayStyle: 'text',
								emptyValue: '',
							},
						],
						sortable: true,
						sortFields: [
							{
								objectTable: '_automation_version',
								fieldName: '_fk__automation_flow__id',
								direction: 'ASC',
							},
						],
					},
					{
						id: 'status',
						headers: [{ id: 'status-header', text: 'Status' }],
						fields: [
							{
								id: 'status-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'executionStatus',
										objectTable: 'automation_execution',
									},
								},
								styleConfig: {
									size: 'sm',
									valueMapping: EXECUTION_STATUS_VALUE_MAPPING,
								},
								displayStyle: 'badge',
							},
						],
						sortable: true,
						sortFields: [
							{
								objectTable: '_automation_execution',
								fieldName: 'executionStatus',
								direction: 'ASC',
							},
						],
						width: 120,
					},
					{
						id: 'batch',
						headers: [{ id: 'batch-header', text: 'Batch' }],
						fields: [
							{
								id: 'batch-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'batch.name',
										objectTable: 'automation_execution',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: { type: 'muted' },
								},
								displayStyle: 'text',
								emptyValue: '-',
							},
						],
						width: 150,
					},
					{
						id: 'started-at',
						headers: [{ id: 'started-at-header', text: 'Started' }],
						fields: [
							{
								id: 'started-at-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'startedAt',
										objectTable: 'automation_execution',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: { type: 'muted' },
								},
								displayStyle: 'date',
								formatOptions: {
									dateStyle: 'short',
									timeStyle: 'short',
								},
							},
						],
						sortable: true,
						sortFields: [
							{
								objectTable: '_automation_execution',
								fieldName: 'startedAt',
								direction: 'DESC',
							},
						],
						width: 150,
					},
					{
						id: 'duration',
						headers: [{ id: 'duration-header', text: 'Duration' }],
						fields: [
							{
								id: 'duration-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'duration',
										objectTable: 'automation_execution',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: { type: 'muted' },
								},
								displayStyle: 'text',
								emptyValue: '-',
							},
						],
						width: 100,
					},
				],
				defaultSortId: 'started-at-desc',
				sortOrder: 0,
			},
		],
		paging: {
			defaultPageSize: 20,
			pageSizeOptions: [10, 20, 50, 100],
		},
		filters: {
			activeFilters: [
				'automationReportKeywords',
				'automationReportStatus',
				'automationReportCreatedDate',
			],
			defaultFilters: {},
		},
		objects: [
			{
				baseUrl: '/automation/executions/:id',
				objectLabel: 'Execution',
				objectTable: '_automation_execution_report',
				selectedFields: [
					{ fieldName: 'id', fieldLabel: 'ID' },
					{ fieldName: 'executionStatus', fieldLabel: 'Status' },
					{ fieldName: 'entityType', fieldLabel: 'Entity Type' },
					{ fieldName: 'entityId', fieldLabel: 'Entity ID' },
					{ fieldName: 'startedAt', fieldLabel: 'Started At' },
					{ fieldName: 'completedAt', fieldLabel: 'Completed At' },
					{ fieldName: 'errorMessage', fieldLabel: 'Error Message' },
				],
			},
		],
	};
